import { apiSchemas } from '@verifime/api-definition';
import { TFormFieldsAndRules_New } from '@verifime/components';
import { stringUtils, TObject } from '@verifime/utils';
import { z } from 'zod';

export type TMedicareCardOwners = {
  id: string;
  name: any;
}[];

export type TOcrData = z.infer<typeof apiSchemas.ScanDocumentApiResponse> &
  Partial<{
    otherIdentityDocument: z.infer<typeof apiSchemas.OtherIdentityDocument>;
  }>;

export const SUPPORT_OCR_DOCUMENT_TYPES = [
  'DriverLicence',
  'MedicareCard',
  'Passport',
  'OtherIdentityDocument',
] as const;
export type TSupportedOcrDocumentType = (typeof SUPPORT_OCR_DOCUMENT_TYPES)[number];

export const documentLabelMapping: { [documentType in TSupportedOcrDocumentType]: string } = {
  DriverLicence: "driver's licence",
  MedicareCard: 'medicare card',
  Passport: 'passport',
  OtherIdentityDocument: 'other identity document',
};

export function objectMedicareCardOwnersToArray(objOwners: TObject): TMedicareCardOwners {
  if (Object.keys(objOwners || {}).length < 1) {
    return [];
  }
  return Object.entries(objOwners).map(([id, name]) => ({ id, name }));
}

export function buildOcrData(
  documentType: TSupportedOcrDocumentType,
  formData: Record<string, any>,
): TOcrData {
  const lowerCaseFirstLetterDocumentType = stringUtils.lowerCaseFirstLetter(documentType);
  return {
    type: documentType,
    [lowerCaseFirstLetterDocumentType]: formData,
  };
}

export function checkIsEmptyForm(formData: Record<string, any>) {
  const copyiedFormData = { ...formData };
  delete copyiedFormData.type;
  const isEmptyForm = Object.values(copyiedFormData).every(
    (value) => !value || Object.keys(value || {}).length < 1,
  );
  return isEmptyForm;
}

// Explicit mapping from TSupportedOcrDocumentType to TOcrData keys
const ocrTypeToDataKey: Record<TSupportedOcrDocumentType, keyof TOcrData> = {
  Passport: 'passport',
  DriverLicence: 'driverLicence',
  MedicareCard: 'medicareCard',
  OtherIdentityDocument: 'otherIdentityDocument',
};

export function filterDocumentData(
  initialData: TOcrData,
  documentType: TSupportedOcrDocumentType,
  formFieldsAndRules: TFormFieldsAndRules_New,
) {
  const dataKey = ocrTypeToDataKey[documentType];
  const rawData = (initialData?.[dataKey] as Record<string, unknown>) || {};
  return Object.values(formFieldsAndRules).reduce<Record<string, unknown>>((filtered, fields) => {
    fields.forEach((field) => {
      if (field.fieldName in rawData) {
        filtered[field.fieldName] = rawData[field.fieldName];
      }
    });
    return filtered;
  }, {});
}
